
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ContactForm from '@/components/ContactForm';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';

const ContactPage = () => {
  const { currentLanguage } = useLanguage();
  const { lang } = useParams<{ lang: string }>();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // Language-specific meta data for better SEO
    const metaTitles = {
      de: `${t.contact} | Future Level - KI-Videoproduktion & Videomarketing Schweiz`,
      en: `${t.contact} | Future Level - AI Video Production & Marketing Switzerland`,
      fr: `${t.contact} | Future Level - Production et Marketing Vidéo IA Suisse`,
      it: `${t.contact} | Future Level - Produzione e Marketing Video IA Svizzera`
    };
    
    // Enhanced meta descriptions with keywords for better search ranking
    const metaDescriptions = {
      de: `Kontaktieren Sie Future Level für professionelle KI-Videomarketing Lösungen. Erhalten Sie innerhalb von 24 Stunden eine persönliche Beratung zu KI-generierten Videos für Social Media und Produktpräsentationen.`,
      en: `Contact Future Level for professional AI video marketing solutions. Get personal consultation within 24 hours on AI-generated videos for social media and product presentations.`,
      fr: `Contactez Future Level pour des solutions professionnelles de marketing vidéo IA. Obtenez une consultation personnelle dans les 24 heures sur les vidéos générées par IA pour les médias sociaux et les présentations de produits.`,
      it: `Contatta Future Level per soluzioni professionali di marketing video IA. Ricevi una consulenza personale entro 24 ore su video generati da IA per social media e presentazioni di prodotti.`
    };
    
    // Enhanced keywords with long-tail variations and location-specific terms
    const metaKeywords = {
      de: "Kontakt Future Level, KI Video Agentur Kontakt, Video Marketing Beratung Schweiz, KI-Videoproduktion Anfrage, Videomarketing Emmen, Online-Videoagentur Schweiz, KI-Marketingvideos Anfrage",
      en: "Contact Future Level, AI Video Agency Contact, Video Marketing Consultation Switzerland, AI Video Production Inquiry, Video Marketing Emmen, Online Video Agency Switzerland, AI Marketing Videos Inquiry",
      fr: "Contact Future Level, Contact Agence Vidéo IA, Consultation Marketing Vidéo Suisse, Demande Production Vidéo IA, Marketing Vidéo Emmen, Agence Vidéo En Ligne Suisse, Demande Vidéos Marketing IA",
      it: "Contatto Future Level, Contatto Agenzia Video IA, Consulenza Marketing Video Svizzera, Richiesta Produzione Video IA, Marketing Video Emmen, Agenzia Video Online Svizzera, Richiesta Video Marketing IA"
    };
    
    // Get the correct canonical URL based on language
    const canonicalUrl = getCanonicalUrl(currentLanguage === 'de' ? 'kontakt' : 
                                   currentLanguage === 'en' ? 'contact' : 
                                   currentLanguage === 'fr' ? 'contact' : 'contatto', 
                                   currentLanguage);
                                   
    // Update SEO meta tags with enhanced information and schema markup
    updateMetaTags({
      title: metaTitles[currentLanguage as keyof typeof metaTitles],
      description: metaDescriptions[currentLanguage as keyof typeof metaDescriptions],
      keywords: metaKeywords[currentLanguage as keyof typeof metaKeywords],
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      schemaType: "ContactPage", // Add schema markup for contact page
      businessType: "OnlineService" // Specify that this is an online service
    });
  }, [currentLanguage, t]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-16 md:pt-20"> {/* Added top padding to account for fixed header */}
        <div className="relative w-full overflow-hidden">
          {/* Background with improved neural pattern */}
          <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#0B0B0B] to-[#1A1F2C]">
            {/* Neural network pattern overlay */}
            <div className="absolute inset-0 bg-[url('/images/grid-pattern.svg')] opacity-10"></div>
            
            {/* Enhanced animated elements */}
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-900/10 blur-3xl animate-pulse-slow"></div>
            <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-purple-800/5 blur-2xl animate-pulse-slow" 
                 style={{ animationDelay: "1.5s" }}></div>
            
            {/* More dynamic background elements */}
            <div className="absolute top-[40%] left-[10%] w-20 h-20 rounded-full bg-purple-700/5 blur-xl animate-pulse-slow"
                 style={{ animationDelay: "0.7s" }}></div>
            <div className="absolute top-[20%] right-[15%] w-16 h-16 rounded-full bg-blue-700/5 blur-xl animate-pulse-slow"
                 style={{ animationDelay: "1.2s" }}></div>
          </div>
          
          <div className="container mx-auto px-4 py-0 relative z-10">
            <div className="text-center mb-6 animate-fade-in">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white glow-effect">
                {t.contact}
              </h1>
              <p className="text-lg text-gray-200 max-w-3xl mx-auto">
                {currentLanguage === 'de' ? 
                  'Bereit, Ihre Video-Marketingstrategie mit KI zu revolutionieren? Füllen Sie unser Kontaktformular aus – wir melden uns innerhalb von 24 Stunden.' :
                currentLanguage === 'en' ?
                  'Ready to revolutionize your video marketing strategy with AI? Fill out our contact form – we will get back to you within 24 hours.' :
                currentLanguage === 'fr' ?
                  'Prêt à révolutionner votre stratégie de marketing vidéo avec l\'IA ? Remplissez notre formulaire de contact – nous vous répondrons dans les 24 heures.' :
                  'Pronto a rivoluzionare la tua strategia di video marketing con l\'IA? Compila il nostro modulo di contatto – ti risponderemo entro 24 ore.'
                }
              </p>
            </div>
            
            <div className="max-w-6xl mx-auto backdrop-blur-sm bg-black/40 p-8 rounded-xl border border-purple-500/30">
              <ContactForm />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
